// ✅ Updated Bookings.jsx - Button & Title Top Left
import React, { useEffect, useState } from 'react';
import { db, functions } from '../firebase';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { addDoc, serverTimestamp } from 'firebase/firestore';


export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'bookings'));
        const fetched = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setBookings(fetched);
      } catch (error) {
        console.error('❌ Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, []);

  const formatDate = (ts) =>
    ts?.seconds ? new Date(ts.seconds * 1000).toLocaleDateString() : '—';

  const isWithinRange = (b) => {
    const start = b.startDate?.seconds ? new Date(b.startDate.seconds * 1000) : null;
    if (!start) return true;
    const from = startDate ? new Date(startDate) : null;
    const to = endDate ? new Date(endDate) : null;
    return (!from || start >= from) && (!to || start <= to);
  };

  const filteredBookings = bookings.filter(b =>
    (selectedStatus === 'all' || b.status === selectedStatus) && isWithinRange(b)
  );

  const exportCSV = () => {
    const headers = ['Renter', 'Listing', 'Start', 'End', 'Total', 'Fee', 'Earnings', 'Insurance', 'Status'];
    const rows = filteredBookings.map(b => [
      b.bookedByName || b.renterId,
      b.listingTitle,
      formatDate(b.startDate),
      formatDate(b.endDate),
      b.totalCost,
      b.platformFee,
      b.hostEarnings,
      b.insuranceType || 'None',
      b.status
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bookings.csv';
    a.click();
  };

  const cancelOrRestore = async (booking) => {
  try {
    const isCanceling = booking.status !== 'canceled' && booking.status !== 'cancelled';
    const newStatus = isCanceling ? 'canceled' : 'confirmed';

    const ref = doc(db, 'bookings', booking.id);
    await updateDoc(ref, { status: newStatus });

    // Optional refund if canceling
    if (isCanceling && booking.paymentIntentId) {
      const refundFn = httpsCallable(functions, 'refundBooking');
      await refundFn({ bookingId: booking.id });
      alert(`Refund issued for booking ${booking.id}`);
    } else {
      alert(`Booking ${isCanceling ? 'canceled' : 'restored'} successfully.`);
    }

    // ✅ Add admin log
    const currentUser = JSON.parse(localStorage.getItem('firebase:authUser'));
    await addDoc(collection(db, 'logs'), {
      action: isCanceling ? 'Canceled Booking' : 'Restored Booking',
      bookingId: booking.id,
      adminEmail: currentUser?.email || 'unknown',
      timestamp: serverTimestamp(),
    });

    setSelectedBooking(null);
    window.location.reload();
  } catch (err) {
    console.error('❌ Error updating booking:', err);
    alert('Failed to update booking.');
  }
};


  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
        ← Back to Dashboard
      </button>
      <h2 style={styles.title}>📦 All Bookings</h2>

      <div style={styles.filters}>
        <label>Status:{' '}
          <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} style={styles.select}>
            <option value="all">All</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="canceled">Canceled</option>
            <option value="refunded">Refunded</option>
          </select>
        </label>
        <label style={{ marginLeft: '1rem' }}>
          From:{' '}
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </label>
        <label style={{ marginLeft: '1rem' }}>
          To:{' '}
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </label>
        <button onClick={exportCSV} style={styles.exportButton}>Export CSV</button>
      </div>

      {loading ? (
        <p style={styles.loading}>Loading bookings...</p>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Renter</th>
                <th style={styles.th}>Listing</th>
                <th style={styles.th}>Dates</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Fee</th>
                <th style={styles.th}>Earnings</th>
                <th style={styles.th}>Insurance</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((b) => (
                <tr key={b.id} style={styles.row} onClick={() => setSelectedBooking(b)}>
                  <td style={styles.td}>{b.bookedByName || b.renterId}</td>
                  <td style={styles.td}>{b.listingTitle}</td>
                  <td style={styles.td}>{formatDate(b.startDate)} → {formatDate(b.endDate)}</td>
                  <td style={styles.td}>${b.totalCost?.toFixed(2) || '—'}</td>
                  <td style={styles.td}>${b.platformFee?.toFixed(2) || '—'}</td>
                  <td style={styles.td}>${b.hostEarnings?.toFixed(2) || '—'}</td>
                  <td style={styles.td}>{b.insuranceType || 'None'}</td>
                  <td style={styles.td}><span style={{ ...styles.status, ...statusColor(b.status) }}>{b.status}</span></td>
                  <td style={styles.td}>{b.receiptUrl ? <a href={b.receiptUrl} target="_blank" rel="noreferrer" style={styles.link}>View</a> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedBooking && (
        <div style={styles.modalOverlay} onClick={() => setSelectedBooking(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Booking Details</h3>
            <pre style={styles.pre}>{JSON.stringify(selectedBooking, null, 2)}</pre>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={() => cancelOrRestore(selectedBooking)}
                style={{
                  ...styles.closeButton,
                  backgroundColor:
                    selectedBooking.status === 'canceled' || selectedBooking.status === 'refunded'
                      ? '#065f46'
                      : '#b91c1c',
                }}
              >
                {selectedBooking.status === 'canceled' || selectedBooking.status === 'refunded'
                  ? 'Restore Booking'
                  : 'Cancel + Refund'}
              </button>
              <button onClick={() => setSelectedBooking(null)} style={styles.closeButton}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  backButton: {
    marginBottom: '0.5rem',
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.4rem 0.8rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#065f46',
    marginBottom: '1.5rem',
  },
  filters: {
    marginBottom: '1rem',
  },
  select: {
    marginLeft: '0.5rem',
    padding: '0.3rem',
    fontSize: '0.9rem',
  },
  exportButton: {
    marginLeft: '1rem',
    padding: '0.4rem 0.8rem',
    backgroundColor: '#065f46',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  loading: {
    fontSize: '1rem',
    color: '#555',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#ffffff',
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    backgroundColor: '#e0f2f1',
    fontWeight: '600',
    fontSize: '0.875rem',
    color: '#000000',
    borderBottom: '1px solid #c8e6c9',
  },
  row: {
    borderBottom: '1px solid #eee',
    cursor: 'pointer',
  },
  td: {
    padding: '12px',
    fontSize: '0.875rem',
    color: '#333',
    whiteSpace: 'nowrap',
  },
  status: {
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  link: {
    color: '#0077cc',
    textDecoration: 'underline',
    fontSize: '0.85rem',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    background: '#fff',
    padding: '2rem',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  pre: {
    fontSize: '0.75rem',
    background: '#f9f9f9',
    padding: '1rem',
    borderRadius: '6px',
    overflowX: 'auto',
  },
  closeButton: {
    marginTop: '1rem',
    backgroundColor: '#065f46',
    color: '#fff',
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
};

function statusColor(status) {
  switch (status) {
    case 'confirmed': return { backgroundColor: '#e6f4ea', color: '#256029' };
    case 'pending': return { backgroundColor: '#fffde7', color: '#9a6700' };
    case 'canceled':
    case 'cancelled': return { backgroundColor: '#fdecea', color: '#b71c1c' };
    default: return { backgroundColor: '#eeeeee', color: '#555' };
  }
}
